// The six digits from the sign-in email.
//
// One box per digit, because that is what people expect a code to look like
// and it makes a mistyped digit obvious. The boxes are a presentation of one
// string: the parent holds the whole code, so pasting, autofill and the
// browser's own one-time-code suggestion all work on it as a unit.

import {useEffect, useRef} from 'react';
import type {ClipboardEvent, KeyboardEvent} from 'react';

export const CODE_LENGTH = 6;

interface CodeInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Called once the last digit lands, so the form can submit itself. */
  onComplete?: (value: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  /** Describes the group for a screen reader, e.g. "Sign-in code". */
  label: string;
  /** Puts the cursor where the next digit goes on arrival, and again after each check. */
  autoFocus?: boolean;
}

function digitsOnly(text: string): string {
  return text.replace(/\D/g, '').slice(0, CODE_LENGTH);
}

export function CodeInput({value, onChange, onComplete, disabled, invalid, label, autoFocus}: CodeInputProps) {
  const boxes = useRef<(HTMLInputElement | null)[]>([]);
  const completed = useRef('');

  // Fires on the value, not on the keystroke, so a paste and a typed sixth
  // digit behave the same. The guard stops a re-render from submitting twice.
  useEffect(() => {
    if (value.length === CODE_LENGTH && completed.current !== value) {
      completed.current = value;
      onComplete?.(value);
    }
    if (value.length < CODE_LENGTH) completed.current = '';
  }, [value, onComplete]);

  function focusBox(index: number) {
    boxes.current[Math.max(0, Math.min(CODE_LENGTH - 1, index))]?.focus();
  }

  // The boxes are disabled while a code is checked, which drops the focus; a
  // code that failed comes back cleared, ready to be typed again.
  useEffect(() => {
    if (autoFocus && !disabled) focusBox(value.length);
    // Only on arrival and when a check ends, not on every digit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocus, disabled]);

  function setDigit(index: number, typed: string) {
    const digits = digitsOnly(typed);
    if (!digits) return;
    // Typing into a box replaces from there; a paste fills the rest.
    const next = digitsOnly(value.slice(0, index) + digits + value.slice(index + digits.length));
    onChange(next);
    focusBox(index + digits.length);
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>, index: number) {
    if (event.key === 'Backspace') {
      event.preventDefault();
      if (value[index]) onChange(value.slice(0, index) + value.slice(index + 1));
      else {
        onChange(value.slice(0, Math.max(0, index - 1)) + value.slice(index));
        focusBox(index - 1);
      }
      return;
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusBox(index - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusBox(index + 1);
    }
  }

  function onPaste(event: ClipboardEvent<HTMLInputElement>, index: number) {
    const pasted = digitsOnly(event.clipboardData.getData('text'));
    if (!pasted) return;
    event.preventDefault();
    setDigit(index, pasted);
  }

  return (
    <div
      className={invalid ? 'code-input code-input-invalid' : 'code-input'}
      role="group"
      aria-label={label}
    >
      {Array.from({length: CODE_LENGTH}, (_, index) => (
        <input
          key={index}
          ref={(node) => {
            boxes.current[index] = node;
          }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          // Only the first box claims the browser's code suggestion; on the
          // others it would offer to fill the whole code into one digit.
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={CODE_LENGTH}
          value={value[index] ?? ''}
          disabled={disabled}
          aria-label={`Digit ${index + 1} of ${CODE_LENGTH}`}
          aria-invalid={invalid || undefined}
          // A password manager has nothing to fill here, and its badge would sit on a digit.
          data-1p-ignore=""
          data-lpignore="true"
          data-bwignore=""
          onChange={(event) => setDigit(index, event.target.value)}
          onKeyDown={(event) => onKeyDown(event, index)}
          onPaste={(event) => onPaste(event, index)}
          onFocus={(event) => event.target.select()}
        />
      ))}
    </div>
  );
}
